import {
    Input,
    Popover,
    Skeleton,
    Table,
    Tag,
    Tooltip,
    Typography,
} from "antd";
import {
    CloseOutlined,
    ImportOutlined,
    QuestionCircleOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import { brandsColumns } from "./brand_columns";
import { getBrands } from "./brands.api";
import { Settings } from "./Settings";
import { useEffect, useMemo, useState } from "react";
import { SORT_DIRECTIONS } from "common/constants";
import {
    isCoolerSearchEnabled,
    setCoolerSearchEnabled,
} from "common/settings";
import {
    clearRecentBrands,
    getRecentBrands,
    isRecentBrandsEnabled,
    setRecentBrands,
    setRecentBrandsEnabled,
} from "common/recentBrands";
import { Content } from "antd/lib/layout/layout";
import "./Brands.scss";
const { Search } = Input;
const { Text } = Typography;

// Builds a predicate for the search field when "cooler search" is on. The
// query is treated as a case-insensitive regular expression — which also
// covers plain substring searches. If the query isn't valid regex syntax
// (e.g. an unbalanced `[`), it falls back to a case-insensitive "contains".
const buildContainsMatcher = (query) => {
    if (!query) {
        return () => true;
    }
    try {
        const regex = new RegExp(query, "i");
        return (text) => !!text && regex.test(text);
    } catch {
        const lowered = query.toLowerCase();
        return (text) => !!text && text.toLowerCase().includes(lowered);
    }
};

const getSearchableFields = (b) => [
    b?.fullName,
    b?.email,
    b?.bizNameOrFullName,
    b?.userId?.toString(),
];

// Example patterns shown in the search field's help popover (cooler search).
const REGEX_EXAMPLES = [
    { pattern: "^On$", description: "exact match" },
    { pattern: "^MTN", description: "starts with" },
    { pattern: "gmail\\.com$", description: "ends with" },
    { pattern: "john|jane", description: "either term" },
];

export const Brands = () => {
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [coolerSearch, setCoolerSearch] = useState(isCoolerSearchEnabled());
    const [recentBrandsOn, setRecentBrandsOn] = useState(
        isRecentBrandsEnabled(),
    );
    const [recentBrandIds, setRecentBrandIds] = useState(getRecentBrands());

    useEffect(() => {
        fetchBrands();
    }, []);

    const fetchBrands = async () => {
        setLoading(true);
        getBrands()
            .then((brands) => {
                setBrands(brands);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const onSearch = (value) => {
        setSearchText(value);
    };

    const onChangeSearch = (e) => {
        onSearch(e?.target?.value);
    };

    const onCoolerSearchChange = (checked) => {
        setCoolerSearchEnabled(checked);
        setCoolerSearch(checked);
    };

    const onRecentBrandsChange = (checked) => {
        setRecentBrandsEnabled(checked);
        setRecentBrandsOn(checked);
        // Turning the setting off discards any tracked brands.
        if (checked) {
            setRecentBrandIds(getRecentBrands());
        } else {
            clearRecentBrands();
            setRecentBrandIds([]);
        }
    };

    // Removes a single brand from the recent-history tags (the tag's X).
    const removeRecentBrand = (userId) => {
        const next = recentBrandIds.filter(
            (id) => Number(id) !== Number(userId),
        );
        setRecentBrands(next);
        setRecentBrandIds(next);
    };

    const [regexHintOpen, setRegexHintOpen] = useState(false);

    // Clicking an example drops it straight into the search input.
    const onPickRegexExample = (pattern) => {
        onSearch(pattern);
        setRegexHintOpen(false);
    };

    const regexSearchHint = (
        <div style={{ maxWidth: 280 }}>
            <Text type="secondary">
                Accepts a case-insensitive regular expression. Click an example
                to use it.
            </Text>
            <div style={{ marginTop: 8 }}>
                {REGEX_EXAMPLES.map(({ pattern, description }) => (
                    <div
                        key={pattern}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 16,
                            padding: "2px 0",
                        }}>
                        <span
                            onClick={() => onPickRegexExample(pattern)}
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                cursor: "pointer",
                            }}>
                            <Text code style={{ cursor: "pointer" }}>
                                {pattern}
                            </Text>
                            <ImportOutlined
                                style={{ color: "rgba(0, 0, 0, 0.45)" }}
                            />
                        </span>
                        <Text type="secondary">{description}</Text>
                    </div>
                ))}
            </div>
        </div>
    );

    // Database names are open-ended, so build the column's filter options
    // from the loaded data and inject them into the static column def.
    const columns = useMemo(() => {
        const databaseFilters = [
            ...new Set(brands.map((b) => b?.databaseName).filter(Boolean)),
        ]
            .sort((a, b) => a.localeCompare(b))
            .map((name) => ({ text: name, value: name }));

        return brandsColumns.map((col) =>
            col.key === "databaseName"
                ? { ...col, filters: databaseFilters }
                : col,
        );
    }, [brands]);

    const dataSource = useMemo(() => {
        if (!searchText) {
            return brands;
        }
        // "Cooler search" reinterprets the same field as a regex / contains
        // query; otherwise it's the plain "starts with" search.
        if (coolerSearch) {
            const matchesQuery = buildContainsMatcher(searchText);
            return brands.filter((b) =>
                getSearchableFields(b).some((field) => matchesQuery(field)),
            );
        }
        const query = searchText.toLowerCase();
        return brands.filter((b) =>
            getSearchableFields(b).some((field) =>
                field?.toLowerCase().startsWith(query),
            ),
        );
    }, [brands, searchText, coolerSearch]);

    // Recent-history tags shown between the search box and the settings cog,
    // most recent first. Names are resolved from the loaded brand list via a
    // userId -> brand Map to keep this O(M) rather than O(N*M).
    const recentBrandTags = useMemo(() => {
        if (!brands.length) {
            return [];
        }
        const brandsById = new Map(
            brands.map((b) => [Number(b?.userId), b]),
        );
        return recentBrandIds.map((id) => {
            const brand = brandsById.get(Number(id));
            return {
                id,
                name:
                    brand?.bizNameOrFullName ||
                    brand?.fullName ||
                    String(id),
                shard: brand?.databaseName,
                shardId: brand?.databaseId,
                role: brand?.role,
                // Full name is only surfaced for team members, not owners.
                fullName:
                    brand?.role === "Team Member" ? brand?.fullName : null,
            };
        });
    }, [brands, recentBrandIds]);

    return (
        <Content style={{ padding: 24 }}>
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    marginBottom: 24,
                }}>
                <Search
                    autoFocus={true}
                    allowClear
                    enterButton={false}
                    value={searchText}
                    onChange={onChangeSearch}
                    onSearch={onSearch}
                    placeholder={
                        coolerSearch
                            ? "Search (contains / regex)"
                            : "Search by name and email"
                    }
                    style={{ width: 300, flexShrink: 0 }}
                    suffix={
                        coolerSearch ? (
                            <Popover
                                content={regexSearchHint}
                                title="Regex search"
                                trigger="click"
                                open={regexHintOpen}
                                onOpenChange={setRegexHintOpen}>
                                <QuestionCircleOutlined
                                    style={{
                                        color: "rgba(0, 0, 0, 0.45)",
                                        cursor: "pointer",
                                    }}
                                />
                            </Popover>
                        ) : undefined
                    }
                />
                {/* Recent-brand tags fill the space between search and the
                    settings cog; whatever doesn't fit on one row is clipped. */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        flex: 1,
                        minWidth: 0,
                        overflow: "hidden",
                    }}>
                    {recentBrandsOn &&
                        (brands.length
                            ? recentBrandTags.map(
                                  ({ id, name, shard, fullName, role }) => (
                                  <Tooltip
                                      key={id}
                                      color="#fff"
                                      title={
                                          <div
                                              style={{
                                                  lineHeight: 1.8,
                                                  color: "rgba(0, 0, 0, 0.88)",
                                              }}>
                                              <div
                                                  style={{
                                                      fontWeight: 600,
                                                      marginBottom: 2,
                                                  }}>
                                                  {name}
                                              </div>
                                              {fullName && (
                                                  <div>
                                                      <span
                                                          style={{
                                                              opacity: 0.55,
                                                          }}>
                                                          Full Name:
                                                      </span>{" "}
                                                      {fullName}
                                                  </div>
                                              )}
                                              <div>
                                                  <span
                                                      style={{
                                                          opacity: 0.55,
                                                      }}>
                                                      Role:
                                                  </span>{" "}
                                                  {role || "—"}
                                              </div>
                                              <div>
                                                  <span
                                                      style={{
                                                          opacity: 0.55,
                                                      }}>
                                                      User ID:
                                                  </span>{" "}
                                                  <Text
                                                      copyable={{
                                                          text: String(id),
                                                      }}>
                                                      {id}
                                                  </Text>
                                              </div>
                                              <div>
                                                  <span
                                                      style={{
                                                          opacity: 0.55,
                                                      }}>
                                                      Shard:
                                                  </span>{" "}
                                                  {shard ? (
                                                      <Text
                                                          copyable={{
                                                              text: shard,
                                                          }}>
                                                          {shard}
                                                      </Text>
                                                  ) : (
                                                      "—"
                                                  )}
                                              </div>
                                          </div>
                                      }>
                                      <Link
                                          to={`/brands/${id}/login`}
                                          style={{ flexShrink: 0 }}>
                                          <Tag
                                              style={{
                                                  marginInlineEnd: 0,
                                                  fontSize: 14,
                                                  lineHeight: "22px",
                                                  padding: 0,
                                                  display: "inline-flex",
                                                  alignItems: "stretch",
                                                  overflow: "hidden",
                                              }}>
                                              <span
                                                  style={{
                                                      padding: "3px 10px",
                                                      cursor: "pointer",
                                                  }}>
                                                  {name}
                                              </span>
                                              <span
                                                  style={{
                                                      width: 1,
                                                      background:
                                                          "rgba(0, 0, 0, 0.18)",
                                                  }}
                                              />
                                              <span
                                                  onClick={(e) => {
                                                      e.preventDefault();
                                                      e.stopPropagation();
                                                      removeRecentBrand(id);
                                                  }}
                                                  style={{
                                                      display: "inline-flex",
                                                      alignItems: "center",
                                                      padding: "0 8px",
                                                      cursor: "pointer",
                                                  }}>
                                                  <CloseOutlined
                                                      style={{ fontSize: 10 }}
                                                  />
                                              </span>
                                          </Tag>
                                      </Link>
                                  </Tooltip>
                              ))
                            : recentBrandIds.map((id) => (
                                  <Skeleton.Button
                                      key={id}
                                      active
                                      size="small"
                                      shape="round"
                                      style={{
                                          width: 110,
                                          flexShrink: 0,
                                      }}
                                  />
                              )))}
                </div>
                <div style={{ flexShrink: 0 }}>
                    <Settings
                        recentBrandsOn={recentBrandsOn}
                        onRecentBrandsChange={onRecentBrandsChange}
                        coolerSearch={coolerSearch}
                        onCoolerSearchChange={onCoolerSearchChange}
                    />
                </div>
            </div>
            <Table
                bordered
                columns={columns}
                dataSource={dataSource}
                loading={loading}
                pagination={{
                    position: ["bottomCenter"],
                    hideOnSinglePage: true,
                    pageSizeOptions: [20, 50, 100],
                    defaultPageSize: 20,
                    // size: "default",
                    showTotal: (total) => `Total ${total} items`,
                }}
                rowKey="userId"
                scroll={{
                    scrollToFirstRowOnChange: true,
                    y: "calc(100vh - 180px)",
                    x: 1000,
                }}
                size="small"
                sortDirections={SORT_DIRECTIONS}
                style={{ width: "100%", height: "100%" }}
            />
        </Content>
    );
};
